import { isResource } from '@medplum/core'
import type { Library } from '@medplum/fhirtypes'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { isOfType, now } from '#src/utils.ts'

export class ExtendedLibrary extends ExtendedResource<Library> {
  static readonly RESOURCE_TYPE = 'Library'

  static isInstance(value: unknown): value is Library | ExtendedLibrary {
    return isResource(value) && isOfType(value, 'Library')
  }

  constructor(
    data?: Omit<Library, 'resourceType' | 'status' | 'type'> &
      Partial<Pick<Library, 'status' | 'type'>>
  ) {
    super({
      status: 'active',
      type: codeableConcept({
        system: 'http://terminology.hl7.org/CodeSystem/library-type',
        code: 'logic-library', // The resource is a shareable library of formalized knowledge. https://hl7.org/fhir/valueset-library-type.html
        display: 'Logic Library',
      }),
      date: now(),
      ...data,
    })
  }

  set status(status: Library['status']) {
    this.resource.status = status
  }

  get typeCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.type)
  }

  get name(): string | undefined {
    return this.resource.name
  }

  set name(name: string) {
    this.resource.name = name
  }

  get title(): string {
    return this.resource.title ?? ''
  }

  set title(title: string) {
    this.resource.title = title
  }

  get topic(): string | undefined {
    return displayFromCodeableConcept(this.resource.topic?.[0])
  }

  get topicCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.topic?.[0])
  }

  get dateString(): string | undefined {
    return this.resource.date
  }

  get version(): string | undefined {
    return this.resource.version
  }

  get publisher(): string | undefined {
    return this.resource.publisher
  }

  get description(): string | undefined {
    return this.resource.description
  }

  set description(description: string) {
    this.resource.description = description
  }

  get authorNames(): string[] | undefined {
    return this.resource.author?.map((a) => a.name ?? '')
  }

  get contentDetails(): string[] | undefined {
    return this.resource.content?.map(
      (c) => `${c.title ?? ''}: ${c.contentType} (${c.url ?? ''})`
    )
  }

  get allowValidatedTools(): boolean {
    return true
  }

  get maxSectionsCount(): number {
    return 50
  }

  get status(): Library['status'] {
    return this.resource.status
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      type: this.typeCode,
      name: this.name,
      title: this.title,
      date: this.dateString,
      version: this.version,
      publisher: this.publisher,
      description: this.description,
      author: this.authorNames,
      content: this.contentDetails,
    }
  }
}
